import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Download, ShieldCheck, FileBox, Loader2, AlertCircle, ArrowLeft, Hash } from "lucide-react";
import SEO from "../components/SEO";

function fmtSize(n) {
  if (!n) return "";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${u[i]}`;
}

export default function DownloadLanding() {
  const { slug } = useParams();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/download/${slug}`)
      .then((r) => { if (active) setFile(r.data); })
      .catch((err) => { if (active) setError(err?.response?.data?.detail || "Soubor nenalezen"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  const handleDownload = async () => {
    if (!file) return;
    setDownloading(true);
    // Fire-and-forget tracking (non-blocking)
    api.post(`/download/${slug}/track`).catch(() => {});
    // Trigger download via hidden anchor
    const a = document.createElement("a");
    a.href = file.download_url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.download = file.filename || "";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => setDownloading(false), 1500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white" data-testid="download-landing">
      <SEO title={file ? `Stáhnout ${file.title}` : "Stahování souboru"} description={file ? `Stáhněte si ${file.title}${file.version ? ` (verze ${file.version})` : ""}.` : ""}/>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-16 pb-24">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-8" data-testid="download-back-home">
          <ArrowLeft className="w-4 h-4"/> Zpět na hlavní stránku
        </Link>

        {loading && (
          <div className="bg-white rounded-3xl border border-zinc-100 p-16 text-center shadow-sm">
            <Loader2 className="w-10 h-10 text-zinc-300 animate-spin mx-auto"/>
          </div>
        )}

        {!loading && error && (
          <div className="bg-white rounded-3xl border border-zinc-100 p-12 text-center shadow-sm" data-testid="download-error">
            <AlertCircle className="w-14 h-14 text-orange-400 mx-auto mb-4"/>
            <h1 className="font-display text-2xl font-bold mb-2">Soubor nenalezen</h1>
            <p className="text-zinc-500 mb-6">{error}</p>
            <Link to="/" className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold">Zpět na úvod</Link>
          </div>
        )}

        {!loading && file && (
          <div className="bg-white rounded-3xl border border-zinc-100 shadow-sm overflow-hidden">
            <div className="p-8 sm:p-12">
              <div className="flex items-start gap-5 mb-8">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shrink-0 shadow-lg shadow-blue-500/20">
                  <FileBox className="w-8 h-8 sm:w-10 sm:h-10 text-white"/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-wider text-blue-600 font-bold mb-1.5">Instalační soubor</div>
                  <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 leading-tight" data-testid="download-title">{file.title}</h1>
                  {file.version ? <div className="text-zinc-500 text-sm mt-1.5">verze {file.version}</div> : null}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8 pb-8 border-b border-zinc-100">
                <Meta label="Velikost" value={fmtSize(file.size) || "—"}/>
                <Meta label="Typ" value={file.content_type?.split("/").pop()?.toUpperCase() || "BIN"}/>
                <Meta label="Stahování" value={file.download_count?.toLocaleString("cs-CZ") || "0"}/>
                <Meta label="Soubor" value={file.filename || "—"} mono/>
              </div>

              {file.notes ? (
                <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-5 mb-8 text-sm text-zinc-700 whitespace-pre-wrap" data-testid="download-notes">
                  {file.notes}
                </div>
              ) : null}

              <button
                onClick={handleDownload}
                disabled={downloading}
                className="btn-primary w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl font-bold text-base shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all disabled:opacity-60"
                data-testid="download-button"
              >
                {downloading ? <Loader2 className="w-5 h-5 animate-spin"/> : <Download className="w-5 h-5"/>}
                <span>{downloading ? "Spouštím stahování…" : `Stáhnout ${fmtSize(file.size) || ""}`.trim()}</span>
              </button>

              <div className="flex items-center justify-center gap-2 mt-5 text-xs text-zinc-500">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500"/>
                <span>Soubor je hostován na Cloudflare R2 — bezpečné připojení HTTPS.</span>
              </div>
            </div>

            <div className="bg-zinc-50/60 px-8 sm:px-12 py-5 border-t border-zinc-100 flex items-center justify-between text-xs text-zinc-500">
              <span className="inline-flex items-center gap-1.5"><Hash className="w-3 h-3"/> /download/{file.slug}</span>
              <span>spravnysmer.eu</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">{label}</div>
      <div className={`text-sm font-semibold text-zinc-900 truncate ${mono ? "font-mono text-xs" : ""}`} title={value}>{value}</div>
    </div>
  );
}
